<template>
  <div>
    <aside
      class="sticky top-0 flex min-h-screen w-[280px] flex-col bg-[linear-gradient(180deg,#0b1220_0%,#10263a_48%,#0f766e_100%)] text-white shadow-[10px_0_30px_rgba(15,23,42,0.16)] max-md:fixed max-md:bottom-0 max-md:left-0 max-md:z-[1200] max-md:transition-transform"
      :class="isOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full'"
    >
      <div class="flex items-center gap-3 border-b border-white/10 px-4 py-4">
        <img
          class="h-11 w-11 rounded-xl bg-white object-cover p-0.5 shadow-[0_12px_28px_rgba(0,0,0,0.22)]"
          src="../assets/Zalo.png"
          alt="Logo"
        />
        <div class="min-w-0">
          <div class="truncate text-[15px] font-black leading-5">
            ZaloOA Phường Hương Trà
          </div>
          <div class="mt-0.5 text-xs font-medium text-slate-300">
            Menu chức năng
          </div>
        </div>
      </div>

      <nav class="flex flex-col gap-1.5 overflow-auto px-3 py-4">
        <RouterLink
          v-for="item in menuItems"
          :key="item.to"
          class="group relative flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold !text-slate-100 !no-underline transition duration-150 visited:!text-slate-100 hover:translate-x-0.5 hover:border-teal-300/30 hover:bg-teal-500/12 hover:!text-white hover:!no-underline focus:outline-none [&.router-link-active]:border-teal-300/45 [&.router-link-active]:!bg-teal-600 [&.router-link-active]:!text-white [&.router-link-active]:shadow-[0_10px_22px_rgba(15,118,110,0.24)]"
          :to="item.to"
          @click="handleNavClick"
        >
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-300/12 text-[15px] !text-teal-200 transition group-hover:bg-teal-300/20 group-hover:!text-teal-100 group-[.router-link-active]:bg-white/18 group-[.router-link-active]:!text-white"
          >
            <span class="pi" :class="item.icon"></span>
          </span>
          <span
            class="min-w-0 flex-1 truncate leading-5 !text-inherit !no-underline"
            >{{ item.label }}</span
          >
        </RouterLink>
      </nav>

      <div
        class="mt-auto flex flex-col gap-3 border-t border-white/10 px-3 pb-4 pt-3.5"
      >
        <Button
          label="Đăng xuất"
          @click="logout"
          icon="pi pi-sign-out"
          iconPos="right"
          class="w-full !min-h-[42px] border-0 !bg-amber-400 !font-extrabold !text-slate-950 shadow-[0_10px_22px_rgba(245,158,11,0.24)] hover:!bg-amber-300"
        />

        <div
          class="border-t border-white/10 pt-2.5 text-center text-[11px] leading-snug text-white/75"
        >
          <div class="flex flex-wrap items-center justify-center gap-1.5">
            <span class="pi pi-map-marker text-xs text-white/70"></span>
            <span>© Copyright 2026 – Phường Hương Trà, Thành phố Đà Nẵng</span>
          </div>
          <div class="flex flex-wrap items-center justify-center gap-1.5">
            <span class="text-xs text-white/45">·</span>
            <span>
<!--              Designed by Mobifone-->
              <!--              <a class="footnote-link" href="mailto:voontv@gmail.com"-->
              <!--                >voontv@gmail.com</a-->
              <!--              >-->
            </span>
          </div>
        </div>
      </div>
    </aside>

    <div
      v-if="isOpen"
      class="fixed inset-0 z-[1100] bg-black/45 md:hidden"
      @click="$emit('close')"
    ></div>
  </div>
</template>

<script lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { useToast } from "@/ui/services/toast";
import router from "@/router";

type MenuItem = { label: string; icon: string; to: string };

export default {
  name: "AppSidebar",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const store = useStore();
    const toast = useToast();

    const permission = computed(() =>
      (store.state.permission || "").toLowerCase(),
    );
    const isAdmin = computed(() => permission.value === "admin");
    const isWardOrDistrict = computed(
      () => permission.value === "ward" || permission.value === "district",
    );

    const menuItems = computed<MenuItem[]>(() => {
      // Tập menu theo logic cũ của TopNavBar.vue
      if (isAdmin.value) {
        return [
          { label: "Gởi tin nhắn", icon: "pi-send", to: "/sendmessage" },
          { label: "Đăng hình ảnh", icon: "pi-upload", to: "/uploadImage" },
          // { label: "Thuế", icon: "pi-dollar", to: "/addtax" },
          // { label: "DS dân cư trong tổ", icon: "pi-bars", to: "/residentList" },
          {
            label: "Quản lý nhóm",
            icon: "pi-plus-circle",
            to: "/managerGroup",
          },
          {
            label: "Quản lý nhận góp ý",
            icon: "pi-envelope",
            to: "/addManagerFeedbackPage",
          },
          {
            label: "Quản lý lịch trực",
            icon: "pi-calendar",
            to: "/fullcalendar",
          },
          { label: "Đăng ký tài khoản", icon: "pi-user-plus", to: "/register" },
          { label: "Quản lý tài khoản", icon: "pi-users", to: "/userlist" },
        ];
      }

      if (isWardOrDistrict.value) {
        return [
          { label: "Gởi tin nhắn", icon: "pi-send", to: "/sendmessage" },
          { label: "Đăng hình ảnh", icon: "pi-upload", to: "/uploadImage" },
          // Tạm thời ẩn chức năng Thuế.
          // { label: "Thuế", icon: "pi-dollar", to: "/addtax" },
          {
            label: "Danh sách dân cư trong tổ",
            icon: "pi-bars",
            to: "/residentList",
          },
          {
            label: "Quản lý nhóm",
            icon: "pi-plus-circle",
            to: "/managerGroup",
          },
        ];
      }

      // Default user
      return [
        { label: "Gởi tin nhắn", icon: "pi-send", to: "/sendmessage" },
        { label: "Đăng hình ảnh", icon: "pi-upload", to: "/uploadImage" },
        {
          label: "Thêm dân cư vào tổ",
          icon: "pi-plus-circle",
          to: "/addPeople",
        },
        {
          label: "Danh sách dân cư trong tổ",
          icon: "pi-bars",
          to: "/residentList",
        },
        {
          label: "Quản lý nhóm",
          icon: "pi-plus-circle",
          to: "/managerGroup",
        },
      ];
    });

    const logout = () => {
      store.dispatch("clearToken");
      store.dispatch("clearPermission");
      emit("close");
      router.push({ name: "login" });
      toast.add({
        severity: "success",
        summary: "Đăng xuất",
        detail: "Đã xóa thông tin đăng nhập thành công",
        life: 1000,
      });
    };

    const handleNavClick = () => {
      // Mobile: đóng sidebar sau khi chọn menu
      emit("close");
    };

    return { menuItems, logout, handleNavClick };
  },
};
</script>
